import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import API from "../../services/SchoolClassService";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as bookActions from "../../redux/actions/bookActions";
import { STATUS, CLASS_TYPES, WEEKDAYS } from "../common/Const";
import { buildSchoolYearRange, getCurrentSchoolYearStartYear } from "../common/schoolYear";
import DashboardCalendar from "./DashboardCalendar";
import SelectorModal from "../common/SelectorModal";
import CreateClassModal from "./CreateClassModal";

const isActiveTeacher = teacher => teacher && teacher.archived !== true && teacher.deleted !== true;

const compareTeachersByName = (left, right) => (
  (left.lastName || "").localeCompare(right.lastName || "", "pl", { sensitivity: "base" }) ||
  (left.firstName || "").localeCompare(right.firstName || "", "pl", { sensitivity: "base" })
);

const formatTeacherOption = teacher => ({
  value: teacher.id,
  text: `${teacher.firstName} ${teacher.lastName}`,
});

const getActiveTeacherOptions = teachers => teachers
  .filter(isActiveTeacher)
  .sort(compareTeachersByName)
  .map(formatTeacherOption);

export const Dashboard = ({
  userId,
  userRole,
  userTeacherId,
  schoolClasses,
  loadSchoolClasses,
  saveSchoolClass,
  teachers,
  loadTeachers,
  students,
  loadStudents,
  classStudents,
  loadClassStudents,
  saveLessonRegister,
  loadLessonRegisters,
  deleteLessonRegister,
  lessonRegister,
  books,
  loadBooks,
  activeSchoolYearStartYear,
  onActiveSchoolYearChange,
}) => {
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const canManageDashboardClasses = userRole !== "TEACHER";
  const [user, setUser] = useState({});
  const [selectedTeacher, setSelectedTeacher] = useState(user);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [status, setStatus] = useState(STATUS.VIEW);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSlotData, setSelectedSlotData] = useState(null);
  const isLessonWithinActiveSchoolYear = (scheduledLesson) => {
    const lessonDate = scheduledLesson.lessonRegister
      ? scheduledLesson.lessonRegister.lessonDetails.date
      : scheduledLesson.defaultLessonDetails.date;

    return lessonDate >= activeSchoolYearRange.fromDate && lessonDate <= activeSchoolYearRange.tillDate;
  };

  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading schoolClasses failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadSchoolClasses]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        alert(`Loading teachers failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    const teacher = userRole === "TEACHER" && userTeacherId
      ? teachers.find(t => t.id === userTeacherId) || teachers.find(t => t.email === userId)
      : teachers.find(t => t.email === userId);
    if (teacher) {
      setUser(teacher);
      setSelectedTeacher({ value: teacher.id, text: `${teacher.firstName} ${teacher.lastName}` });
    }
  }, [teachers, userId, userRole, userTeacherId]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading lessonRegisters failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadLessonRegisters]);

  useEffect(() => {
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading classStudents failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadClassStudents]);

  useEffect(() => {
    if (students.length === 0) {
      loadStudents().catch((error) => {
        alert(`Loading students failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (canManageDashboardClasses && books.length === 0) {
      loadBooks().catch((error) => {
        alert(`Loading books failed ${error}`);
      });
    }
  }, [books.length, canManageDashboardClasses, loadBooks]);

  useEffect(() => {
    const updateScheduledClasses = () => {
      const filteredClasses = schoolClasses.filter((schoolClass) => {
        const classFrom = schoolClass.fromDate;
        const classTill = schoolClass.tillDate || schoolClass.fromDate;

        if (classTill < activeSchoolYearRange.fromDate) return false;
        if (classFrom > activeSchoolYearRange.tillDate) return false;

        return schoolClass.teacherId === selectedTeacher.value;
      });

      const generatedClasses = filteredClasses.map((schoolClass) => {
        const scheduledLessons = API.generateScheduledClasses(
          schoolClass,
          lessonRegister,
          students,
          classStudents,
        ).filter(isLessonWithinActiveSchoolYear);

        return {
          scheduledLessons,
        };
      }).filter(scheduledClass => scheduledClass.scheduledLessons.length > 0);
      setScheduledClasses(generatedClasses);
    };

    updateScheduledClasses();
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    classStudents,
    lessonRegister,
    schoolClasses,
    selectedTeacher,
    students,
  ]);

  const handleTeacherSelect = (option) => {
    setSelectedTeacher(option);
    setIsModalOpen(false);
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .then((savedRegister) => {
        setStatus(STATUS.VIEW);
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleCreateClass = (classData) => {
    const classPayload = {
      ...classData,
      fromDate: classData.fromDate || moment().format('YYYY-MM-DD'),
    };
    
    return saveSchoolClass(classPayload)
      .then(() => {
        loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
        setIsCreateModalOpen(false);
      });
  };

  const handleVisibleDateChange = (visibleDate) => {
    const nextSchoolYearStartYear = getCurrentSchoolYearStartYear(visibleDate);

    if (nextSchoolYearStartYear !== activeSchoolYearStartYear) {
      onActiveSchoolYearChange(nextSchoolYearStartYear);
    }
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title col-block mb-0">
        Witaj {user.firstName || userId}
      </h2>
      {canManageDashboardClasses && (
      <h4 className="col-block d-flex align-items-center mb-3">
        Oglądasz: &nbsp;
        {selectedTeacher.text || `${userId}`}
        <button
          type="button"
          className="manage-select-button"
          onClick={() => setIsModalOpen(true)}
        >
          Wybierz nauczyciela
        </button>
      </h4>
      )}
      {canManageDashboardClasses && (
      <SelectorModal
        title="Wybierz nauczyciela"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        values={getActiveTeacherOptions(teachers)}
        onSelect={option => handleTeacherSelect(option)}
        selectedIDs={[selectedTeacher.value || user.id]}
        layout="grid"
      />
      )}
      <DashboardCalendar
        scheduledClasses={scheduledClasses}
        teachers={teachers}
        students={students}
        onSubmit={handleRegisterUpdate}
        deleteLessonRegister={deleteLessonRegister}
        onVisibleDateChange={handleVisibleDateChange}
        userRole={userRole}
        userTeacherId={userTeacherId}
      />
      <CreateClassModal
        isOpen={isCreateModalOpen}
        initialDate={selectedSlotData?.date}
        initialTime={{ start: selectedSlotData?.startTime, end: selectedSlotData?.endTime }}
        initialDayOfWeek={selectedSlotData?.dayOfWeek}
        initialTeacherId={selectedTeacher.value}
        classTypes={CLASS_TYPES}
        teachers={teachers}
        books={books}
        weekdays={WEEKDAYS}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateClass}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  lessonRegister: state.lessonRegister,
  books: state.books,
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  saveSchoolClass: schoolClassActions.saveSchoolClass,
  loadBooks: bookActions.loadBooks,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
